import { Asset, ValueCountEntry } from "../models/assets";
import { AssetsAsyncApiResponse } from "../models/asyncJobs";
import { Category } from "../models/categories";
import { ModelToAssetsSyncJob } from "../models/extraction";
import {
  AssetModelSyncRecordsResponse,
  AssetsModelMappingConflictGroup,
  AssetsModelMappings,
  AssetsModelMappingsDTO,
  BatchCountModelObjectsResponse,
  BatchCreateAssetsModelMappingsRequest,
  BatchCreateRequest,
  BatchPatchAssetsModelMappingsRequest,
  ModelAssetDiffCounts,
  ModelAssetDiffs,
  ModelFieldDefinition,
  ModelFieldDefinitionsResponse,
  ModelFieldValue,
  ModelProcessingStatus,
  ModelSyncContainer,
  ModelSyncContainerDTO,
  QueryModelObjectsResponse,
  SimpleModelQueryV1,
  SimpleModelQueryV1Expression,
} from "../models/modelIntegration";
import {
  AssetsPaginatedResponse,
  AssetsResponse,
  CursorState,
  ProjectId,
} from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

export const getModelIntegrationClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => ({
  batchCreateModelSyncContainers: (
    models: BatchCreateRequest[],
    {
      includeModelMetadata,
      includeModelSyncState,
    }: {
      includeModelMetadata?: boolean;
      includeModelSyncState?: boolean;
    } = {}
  ): Promise<AssetsResponse<ModelSyncContainerDTO[]>> => {
    const searchString = buildSearchString({
      includeModelMetadata,
      includeModelSyncState,
    });

    return http.post<AssetsResponse<ModelSyncContainerDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers:batch-create${searchString ? `?${searchString}` : ""}`,
      { body: JSON.stringify(models) }
    );
  },
  listModelSyncContainers: (
    cursorState?: CursorState,
    {
      includeModelMetadata,
      includeTrashedModelMetadata,
      includeModelSyncState,
      ids,
      limit,
    }: {
      includeModelMetadata?: boolean;
      includeTrashedModelMetadata?: boolean;
      includeModelSyncState?: boolean;
      ids?: ModelSyncContainerDTO["id"][];
      limit?: number;
    } = {}
  ): Promise<AssetsPaginatedResponse<ModelSyncContainerDTO[]>> => {
    const searchString = buildSearchString({
      cursorState,
      includeModelMetadata,
      includeTrashedModelMetadata,
      includeModelSyncState,
      "filter[id]": ids,
      limit,
    });

    return http.get<AssetsPaginatedResponse<ModelSyncContainerDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers${searchString ? `?${searchString}` : ""}`
    );
  },
  batchDeleteModelSyncContainers: (
    modelSyncContainerIds: ModelSyncContainer["id"][]
  ) =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers:batch-delete`,
      { body: JSON.stringify({ ids: modelSyncContainerIds }) }
    ),
  listAssetModelMappings: ({
    modelSyncContainerId,
    includeMappingMetadata,
    includeDeleted,
    existingToRestore,
    categoryInternalId,
    limit,
    cursorState,
  }: {
    modelSyncContainerId?: ModelSyncContainer["id"];
    includeMappingMetadata?: boolean;
    includeDeleted?: boolean;
    existingToRestore?: boolean;
    categoryInternalId?: Category["internalId"];
    limit?: number;
    cursorState?: CursorState;
  }): Promise<AssetsPaginatedResponse<AssetsModelMappingsDTO[]>> => {
    const searchString = buildSearchString({
      includeMappingMetadata,
      includeDeleted,
      limit,
      cursorState,
      "filter[modelSyncContainerId]": modelSyncContainerId,
      "filter[existingToRestore]": existingToRestore,
      "filter[assetsContainer.categoryId]": categoryInternalId,
    });

    return http.get<AssetsPaginatedResponse<AssetsModelMappingsDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/assets-model-mappings${searchString ? `?${searchString}` : ""}`
    );
  },
  batchCreateAssetModelMappings: (
    mappings: BatchCreateAssetsModelMappingsRequest[]
  ): Promise<AssetsResponse<AssetsModelMappingsDTO[]>> =>
    http.post<AssetsResponse<AssetsModelMappingsDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/assets-model-mappings:batch-create`,
      {
        body: JSON.stringify(mappings),
      }
    ),
  get: async (
    id: ModelSyncContainer["id"],
    includeModelMetadata?: boolean,
    includeModelSyncState?: boolean
  ): Promise<ModelSyncContainerDTO | undefined> => {
    const searchString = buildSearchString({
      "filter[id]": id,
      includeModelMetadata,
      includeModelSyncState,
    });

    const response = await http.get<
      AssetsPaginatedResponse<ModelSyncContainerDTO[]>
    >(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers${searchString ? `?${searchString}` : ""}`
    );

    return response.results[0];
  },
  batchUpdateAssetModelMappings: (
    mappings: BatchPatchAssetsModelMappingsRequest
  ): Promise<Record<AssetsModelMappings["id"], AssetsModelMappings>> =>
    http.patch<Record<AssetsModelMappings["id"], AssetsModelMappings>>(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/assets-model-mappings:batch-patch`,
      { body: JSON.stringify(mappings) }
    ),
  fetchQueryableModelFields: (
    modelSyncContainerId: ModelSyncContainer["id"]
  ): Promise<ModelFieldDefinitionsResponse> =>
    http.get(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers/${modelSyncContainerId}/model-field-definitions/queryable-fields`
    ),
  fetchModelFieldValues: async (
    modelSyncContainerId: ModelSyncContainer["id"],
    modelFieldKey: ModelFieldDefinition["key"],
    searchTokens?: string[],
    query?: SimpleModelQueryV1,
    limit?: number,
    cursorState?: CursorState
  ): Promise<
    AssetsAsyncApiResponse<
      AssetsPaginatedResponse<ValueCountEntry<ModelFieldValue>[]>
    >
  > =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers/${modelSyncContainerId}/model-field-values:query`,
      {
        body: JSON.stringify({
          modelFieldKey,
          searchTokens,
          query,
          limit,
          cursorState,
        }),
      }
    ),
  batchCountModelObjectsByMappingIds: async (
    mappingIds: AssetsModelMappings["id"][]
  ): Promise<AssetsAsyncApiResponse<BatchCountModelObjectsResponse>> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-objects:batch-count`,
      { body: JSON.stringify({ mappingIds }) }
    ),
  batchCountModelObjectsByQueries: async (
    queries: (SimpleModelQueryV1 & {
      modelSyncContainerId: ModelSyncContainer["id"];
    })[]
  ): Promise<AssetsAsyncApiResponse<BatchCountModelObjectsResponse>> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-objects:batch-count`,
      { body: JSON.stringify({ queries }) }
    ),
  queryModelObjects: async ({
    modelSyncContainerId,
    modelQuery,
    limit,
    cursorState,
    options,
  }: {
    modelSyncContainerId: ModelSyncContainer["id"];
    modelQuery?: SimpleModelQueryV1Expression;
    limit?: number;
    cursorState?: CursorState;
    options?: {
      includeProps?: boolean;
      includeViews?: boolean;
      includeDetachmentMetadataForCategory?: Category["id"];
    };
  }): Promise<AssetsAsyncApiResponse<QueryModelObjectsResponse>> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-objects:query`,
      {
        body: JSON.stringify({
          query: {
            modelSyncContainerId,
            modelQuery: modelQuery ? modelQuery : undefined,
            entityType: modelQuery
              ? "simple_model_query_v1"
              : "model_sync_container_query_v1",
          },
          limit,
          cursorState,
          options,
        }),
      }
    ),
  syncModelToAssets: async (
    modelSyncContainerId: ModelSyncContainer["id"],
    detachmentSpecifications?: Record<AssetsModelMappings["id"], boolean>
  ): Promise<ModelToAssetsSyncJob> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/sync-model-to-assets`,
      {
        body: JSON.stringify({
          modelSyncContainerId,
          options: {
            createProgressTrackingChannel: true,
            ...(detachmentSpecifications
              ? {
                  assetsModelMappingDetachmentSpecifications: Object.entries(
                    detachmentSpecifications
                  ).map(([assetsModelMappingId, deleteAssetsOnDetachment]) => ({
                    assetsModelMappingId,
                    deleteAssetsOnDetachment,
                  })),
                }
              : {}),
          },
        }),
      }
    ),
  batchDeleteAssetsModelMappings: async (
    mappingIds: AssetsModelMappings["id"][]
  ): Promise<undefined> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/assets-model-mappings:batch-delete`,
      {
        body: JSON.stringify({ ids: mappingIds }),
      }
    ),
  getModelMappingConflicts: async (
    modelSyncContainerId: ModelSyncContainer["id"]
  ): Promise<
    AssetsAsyncApiResponse<
      AssetsPaginatedResponse<AssetsModelMappingConflictGroup[]>
    >
  > =>
    http.get(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers/${modelSyncContainerId}/assets-model-mapping-conflicts`
    ),
  queryAssetModelSyncRecords: async ({
    modelSyncContainerIds,
    assetsModelMappingIds,
    assetIds,
    attachedStates,
    cursorState,
    limit,
    options,
  }: {
    modelSyncContainerIds?: ModelSyncContainer["id"][];
    assetsModelMappingIds?: AssetsModelMappings["id"][];
    assetIds?: Asset["id"][];
    attachedStates?: ["attached" | "detached"];
    cursorState?: CursorState;
    limit?: number;
    options?: {
      includeDeleted?: boolean;
      includeViews?: boolean;
      includeDetachmentMetadata?: boolean;
    };
  }): Promise<AssetModelSyncRecordsResponse> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v2")}/${
        Endpoints.ModelIntegration
      }/asset-model-sync-records:query`,
      {
        body: JSON.stringify({
          query: {
            entityType: "asset_model_sync_records_atomic_query_v2",
            includeFilters: {
              ...(modelSyncContainerIds?.length
                ? { modelSyncContainerIds }
                : {}),
              ...(assetsModelMappingIds?.length
                ? { assetsModelMappingIds }
                : {}),
              ...(assetIds?.length ? { assetIds } : {}),
              attachedStates,
            },
            excludeFilters: {},
          },
          cursorState,
          limit,
          options,
        }),
      }
    ),
  getModelAssetDiffs: async (
    assetsModelMappingIds: AssetsModelMappings["id"][],
    cursorState?: CursorState,
    limit?: number
  ): Promise<
    AssetsAsyncApiResponse<AssetsPaginatedResponse<ModelAssetDiffs>>
  > =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-to-assets-diffs:query`,
      {
        body: JSON.stringify({
          ...(cursorState ? { cursorState } : {}),
          ...(limit ? { limit } : {}),
          query: {
            entityType: "assets_model_mapping_ids_query_v1",
            assetsModelMappingIds,
          },
          options: { includeViews: false },
        }),
      }
    ),
  batchCountModelAssetDiffs: async (
    assetsModelMappingIds: AssetsModelMappings["id"][]
  ): Promise<AssetsAsyncApiResponse<AssetsResponse<ModelAssetDiffCounts[]>>> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v1")}/${
        Endpoints.ModelIntegration
      }/model-to-assets-diffs:batch-count`,
      {
        body: JSON.stringify({
          query: {
            entityType: "assets_model_mapping_ids_query_v1",
            assetsModelMappingIds,
          },
          options: {},
        }),
      }
    ),
  batchGetProcessingStatus: async (
    modelSyncContainerIds: ModelSyncContainer["id"][]
  ): Promise<AssetsResponse<ModelProcessingStatus[]>> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v2")}/${
        Endpoints.ModelIntegration
      }/model-sync-containers/processing-status:batch-get`,
      { body: JSON.stringify({ ids: modelSyncContainerIds }) }
    ),
});

/**
 * The shape returned by {@link getModelIntegrationClient}.
 * Exported so consumers can type variables without spelling out
 * `ReturnType<typeof getModelIntegrationClient>`.
 */
export type ModelIntegrationClient = ReturnType<
  typeof getModelIntegrationClient
>;
